import { eAnimAssets, eModelAsset, Game, rnd2 } from "../../Game";
import { Ball, eKickOffResult, KickOffResEstimate } from "../Player/Ball";
import { ePhysicsObjects, Stadium } from "../Stadium/Stadium";
import { Gameplay } from "../Gameplay";
import { CatchAnimBase, MissAnimBase } from "./AnimBase";
import { AnimSideStep } from "./AnimSideStep";
import { RoundManager } from "../Round/RoundManager";
import { Rectangle } from "../../Utils/2D Shapes/Rectangle";
import { Anim1 } from "./Anim1";
import { Anim2 } from "./Anim2";
import { Anim3 } from "./Anim3";
import { WrappedArray } from "../../Utils/Collections/WrapperArray";
import { Anim4 } from "./Anim4";
import { MissAnim1 } from "./MissAnim1";



export const enum eGoalieCatchAnimProcessor {
    anim1,
    anim2,
    anim3,
    anim4
}



export const enum eGoalieCatchAnim {
    catchUp,
    catchDown,
    jumpUp1,
    jumpUp2,
    lowSideJump,
    sideJump
}



export interface GoalieCatchAnim {
    animId: eGoalieCatchAnim;
    /**
     * Index objektu animace
     */
    animProcessor: eGoalieCatchAnimProcessor;
    /**
     * Jmeno assetu s animaci
     */
    animName: string;
    /**
     * Offset spusteni animace od casu pruniku mice
     */
    animTimeOffset?: number;
    /**
     * Oblasti, ve kterych je mozne pouzit tuto animaci pro chytnuti mice (originX, originY, width, height)
     */
    catchAreas: pc.Vec4[];
    /**
     * Horizontalni vzdalenost mice od brankare, po kterou neni nutne posouvat brankare do strany
     */
    noHMoveZone: { originX: number; halfWidth: number }
    /**
     * True, pokud nema dojit k presunu entity brankare po ose Y (musi to podporovat sama animace)
     */
    noVMove?: boolean;
    /**
     * Cim vyssi hodnota, tim vyssi priorita. Pokud nezadano, tak priorita 0
     * Animace s vyssi prioritou maji prednost pred animacemi s nizsi prioritou
     */
    priority?: number;

}



export const enum eGoalieMissAnimProcessor {
    anim1
}



export const enum eGoalieMissAnim {
    jumpUp,
    lowSideJump,
    sideJump
}



export interface GoalieMissAnim {
    animId: eGoalieMissAnim,
    animProcessor: eGoalieMissAnimProcessor;
    animName: string,
    animTimeOffset: number;
    animSwappedDir: boolean;
    areas: pc.Vec4[];
    priority?: number;
}



export class Goalie {
    /**
     * Normal vektor pro odraz mice od brankare
     */
    static readonly BALL_REFLECT_NORMAL_VEC = new pc.Vec3(0, 0, 1);

    private static readonly MISS_ANIMS: GoalieMissAnim[] = [
        {
            animId: eGoalieMissAnim.jumpUp,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieJumpUp2,
            animTimeOffset: (20 / 30) * 1000,
            animSwappedDir: true,
            areas: [new pc.Vec4(0.55, 2, 0.8, 1)]
        },
        {
            animId: eGoalieMissAnim.sideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (20 / 30) * 1000,
            animSwappedDir: false,
            areas: [new pc.Vec4(2.305, 2.1, 2.74, 0.8)]
        },
        {
            animId: eGoalieMissAnim.sideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (20 / 30) * 1000,
            animSwappedDir: false,
            areas: [new pc.Vec4(3.105, 0.45, 1.11, 1)]
        },
        {
            animId: eGoalieMissAnim.sideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (15 / 30) * 1000,
            animSwappedDir: false,
            areas: [new pc.Vec4(2.46, 1.313, 2.4, 0.8)]
        },
        {
            animId: eGoalieMissAnim.lowSideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (10 / 30) * 1000,
            animSwappedDir: false,
            areas: [new pc.Vec4(1.6, 0.45, 1.9, 1)]
        },
        {
            animId: eGoalieMissAnim.lowSideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: 800,
            animSwappedDir: true,
            areas: [new pc.Vec4(0, 1.25, 1.6, 2.5)]
        },
        {
            animId: eGoalieMissAnim.lowSideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (15 / 30) * 1000,
            animSwappedDir: false,
            areas: [new pc.Vec4(1.03, 1.32, 0.46, 0.8)],
            priority: -1
        },
        {
            animId: eGoalieMissAnim.lowSideJump,
            animProcessor: eGoalieMissAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: 800,
            animSwappedDir: true,
            areas: [new pc.Vec4(0.575, 0.47, 0.2, 1)],
        },
    ];

    /**
     * Regiony pro chytnuti mice
     */
    private static readonly CATCH_ANIMS: GoalieCatchAnim[] = [
        // Chyceni na miste horni cast tela
        {
            animId: eGoalieCatchAnim.catchUp,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieCatchUp,
            animTimeOffset: (10 / 30) * 1000,
            catchAreas: [new pc.Vec4(0, 1.4, 3, 0.5)],
            noHMoveZone: { originX: 0, halfWidth: 0.15 },
        },
        // Chyceni na miste dolni cast tela
        {
            animId: eGoalieCatchAnim.catchDown,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieCatchDown,
            animTimeOffset: (12 / 30) * 1000,
            catchAreas: [new pc.Vec4(0, 0.9, 4.2, 0.5)],
            noHMoveZone: { originX: 0, halfWidth: 0.15 },
        },
        {
            animId: eGoalieCatchAnim.catchDown,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieCatchDown,
            animTimeOffset: (20 / 30) * 1000,
            catchAreas: [new pc.Vec4(0, 0.46, 2, 0.38)],
            noHMoveZone: { originX: 0, halfWidth: 0.15 },
        },
        // Chyceni ve svislem vyskoku
        {
            animId: eGoalieCatchAnim.jumpUp1,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieJumpUp1,
            animTimeOffset: (20 / 30) * 1000,
            catchAreas: [new pc.Vec4(0, 2.1, 1, 0.9)],
            noHMoveZone: { originX: 0, halfWidth: 0.25 },
        },
        // Chyceni ve svislem vyskoku (mirne do strany)
        {
            animId: eGoalieCatchAnim.jumpUp2,
            animProcessor: eGoalieCatchAnimProcessor.anim2,
            animName: eAnimAssets.goalieJumpUp2,
            animTimeOffset: (25 / 30) * 1000,
            catchAreas: [new pc.Vec4(1.93, 2.19, 3.46, 0.5)],
            noHMoveZone: { originX: 0.45, halfWidth: 0.25 },
            priority: 1
        },
        {
            animId: eGoalieCatchAnim.jumpUp2,
            animProcessor: eGoalieCatchAnimProcessor.anim2,
            animName: eAnimAssets.goalieJumpUp2,
            animTimeOffset: (25 / 30) * 1000,
            catchAreas: [new pc.Vec4(1.91, 1.79, 3.5, 0.3)],
            noHMoveZone: { originX: 0.31, halfWidth: 0.15 },
        },
        {
            animId: eGoalieCatchAnim.jumpUp2,
            animProcessor: eGoalieCatchAnimProcessor.anim2,
            animName: eAnimAssets.goalieJumpUp2,
            animTimeOffset: (25 / 30) * 1000,
            catchAreas: [new pc.Vec4(1.89, 1.59, 3.54, 0.1)],
            noHMoveZone: { originX: 0.27, halfWidth: 0.15 },
        },
        // Nizky skok do strany
        {
            animId: eGoalieCatchAnim.lowSideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (30 / 30) * 1000,
            catchAreas: [new pc.Vec4(0, 0.135, 2.2, 0.27)],
            noHMoveZone: { originX: 0, halfWidth: 0.6 },
        },
        {
            animId: eGoalieCatchAnim.lowSideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (19 / 30) * 1000,
            catchAreas: [new pc.Vec4(0.56, 0.4, 0.5, 0.8)],
            noHMoveZone: { originX: 0.56, halfWidth: 0.2 },
            priority: 2
        },
        {
            animId: eGoalieCatchAnim.lowSideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (24 / 30) * 1000,
            catchAreas: [new pc.Vec4(0.93, 0.6, 0.4, 0.3)],
            noHMoveZone: { originX: 0.93, halfWidth: 0.2 },
            priority: 2
        },
        {
            animId: eGoalieCatchAnim.lowSideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim1,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (30 / 30) * 1000,
            catchAreas: [new pc.Vec4(1.25, 0.325, 0.7, 0.65)],
            noHMoveZone: { originX: 1.25, halfWidth: 0.35 },
            priority: 2
        },
        {
            animId: eGoalieCatchAnim.lowSideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim4,
            animName: eAnimAssets.goalieLowSideJump,
            animTimeOffset: (33 / 30) * 1000,
            catchAreas: [new pc.Vec4(2.1, 0.35, 1.12, 0.7)],
            noHMoveZone: { originX: 1.82, halfWidth: 0.28 },
            priority: 3
        },
        // Skok do strany
        {
            animId: eGoalieCatchAnim.sideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim3,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (25 / 30) * 1000,
            catchAreas: [new pc.Vec4(1.78, 1.3, 0.6, 0.5)],
            noHMoveZone: { originX: 1.78, halfWidth: 0.3 },
            noVMove: true,
            priority: 2
        },
        {
            animId: eGoalieCatchAnim.sideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim3,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (29 / 30) * 1000,
            catchAreas: [new pc.Vec4(2.865, 1.39, 1.59, 0.93)],
            noHMoveZone: { originX: 2.27, halfWidth: 0.2 },
            priority: 2
        },
        {
            animId: eGoalieCatchAnim.sideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim3,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (34 / 30) * 1000,
            catchAreas: [new pc.Vec4(2.865, 0.76, 1.59, 0.4)],
            noHMoveZone: { originX: 2.47, halfWidth: 0.4 },
            noVMove: true,
            priority: 1
        },
        {
            animId: eGoalieCatchAnim.sideJump,
            animProcessor: eGoalieCatchAnimProcessor.anim3,
            animName: eAnimAssets.goalieSideJump,
            animTimeOffset: (38 / 30) * 1000,
            catchAreas: [new pc.Vec4(3.08, 0.28, 1.16, 0.56)],
            noHMoveZone: { originX: 2.75, halfWidth: 0.25 },
            noVMove: true,
            priority: 1
        },
    ];

    /**
     * Entita brankare
     */
    get entity(): pc.Entity { return this._entity; }
    private _entity: pc.Entity;
    /**
     * Entity koliznich krabic
     */
    private _colBoxEntites: pc.Entity[];
    /**
     * Uzly v krabicove verzi brankare napojene na kolizni krabice
     */
    private _colBoxNodes: pc.GraphNode[];

    private _sideSteps: AnimSideStep;
    /**
     * Seznam animaci pro chytnuti mice
     */
    private _catchAnims: CatchAnimBase[];
    /**
     * Seznam animaci pro vyhnuti se mici
     */
    private _missAnims: MissAnimBase[];
    /**
     * Aktualni animace pro chytnuti mice
     */
    private _curAnim: CatchAnimBase | MissAnimBase;
    /**
     * Typ aktualni animace (0 = catch, 1 = miss)
     */
    private _curAnimType: number;
    /**
     * Animace, ktere je mozne pouzit pro chyceni mice na cilove pozici
     */
    private _validAnims: WrappedArray<GoalieCatchAnim | GoalieMissAnim>;

    private _validAnimsMask: number;
    private _validAnimsPriority: number;

    private _targetBallPos = new pc.Vec2();

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        let assets = Game.app.assets;

        // Entita brankare
        this._entity = new pc.Entity();
        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: assets.find(eModelAsset.goalie, pc.eAssetType.model),
            castShadows: true,
            receiveShadows: false,
            static: false
        });

        this._entity.addComponent(pc.eCompomentType.animation, {
            assets: [
                assets.find(eAnimAssets.goalieIdle, pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieSideStep, pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieCatchDown + "L", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieCatchDown + "R", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieCatchUp + "L", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieCatchUp + "R", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieJumpUp1 + "L", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieJumpUp1 + "R", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieJumpUp2 + "L", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieJumpUp2 + "R", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieLowSideJump + "L", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieLowSideJump + "R", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieSideJump + "L", pc.eAssetType.animation),
                assets.find(eAnimAssets.goalieSideJump + "R", pc.eAssetType.animation),
            ],
            speed: 1,
            activate: true,
            loop: true
        });

        Game.app.root.addChild(this._entity);

        // Vytvorit kolizni entity z brabicovych mesh instanci brankare
        this._colBoxEntites = [];
        this._colBoxNodes = [];

        /*let mat = new pc.StandardMaterial();
        mat.diffuse.set(0, 0, 1);
        mat.update();*/

        this._entity.model.meshInstances.forEach((meshIns: pc.MeshInstance): void => {
            let node = meshIns.node;

            if (node.name != "goalie") {
                meshIns.visible = false;

                let box = new pc.Entity();
                let size = node.getLocalScale().divScalar(100);

                box.addComponent(pc.eCompomentType.collision, {
                    type: pc.eCollisionVolumeType.box,
                    halfExtents: new pc.Vec3().copy(size).divScalar(2)
                });
                /*box.addComponent(pc.eCompomentType.model, {
                    type: pc.eModelType.box,
                    castShadows: false,
                    receiveShadows: false,
                    material: mat
                });*/

                box.setLocalScale(size);
                box.collision.on("triggerenter", this.handleColBoxTriggerEnter, this);

                Game.app.root.addChild(box);

                this._colBoxEntites.push(box);
                this._colBoxNodes.push(node);
            }
        });

        // Animace
        this._sideSteps = new AnimSideStep(this);

        this._catchAnims = [];
        this._catchAnims[eGoalieCatchAnimProcessor.anim1] = new Anim1(this);
        this._catchAnims[eGoalieCatchAnimProcessor.anim2] = new Anim2(this);
        this._catchAnims[eGoalieCatchAnimProcessor.anim3] = new Anim3(this);
        this._catchAnims[eGoalieCatchAnimProcessor.anim4] = new Anim4(this);

        this._missAnims = [];
        this._missAnims[eGoalieMissAnimProcessor.anim1] = new MissAnim1(this);

        this._validAnims = new WrappedArray<GoalieCatchAnim | GoalieMissAnim>();

        // Zachytavat vykopnuti mice
        Gameplay.instance.events.on(Ball.EVENT_KICKED_OFF1, this.handleKickOff, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(): void {
        let rs = RoundManager.instance.roundSetup;

        // Nastavit pozici
        if (!rs.noGoalie) {
            this._entity.setPosition(rs.goalieX, 0, Stadium.GOALIE_Z);

            // Idle animace
            this._entity.enabled = true;
            this._entity.animation.play(eAnimAssets.goalieIdle);
            this._entity.animation.loop = true;
        }
        else {
            this._entity.enabled = false;
        }

        // Neni zadna animace chytnuti mice
        this._sideSteps.reset();
        this._curAnim = null;
    }

    //-------------------------------------------------------------------------
    /**
     * Posun brankare do strany (po zemi) o zadanou vzdalenost.
     * @param dis Vzdalenost, o kterou se ma brankar posunout na ose X
     * @param endTime Cas, kdy ma byt posun dokoncen
     */
    //-------------------------------------------------------------------------
    moveToSide(dis: number, endTime: number): void {
        this._sideSteps.init(dis, endTime);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    startAnimation(name: string, blendTime: number, loop: boolean, speed: number = 1): void {
        this._entity.animation.play(name, blendTime);
        this._entity.animation.loop = loop;
        this._entity.animation.speed = speed;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (!this._entity.enabled)
            return;

        // Pohyb do strany po zemi
        this._sideSteps.update();

        // Animace chytnuti mice
        if (this._curAnim)
            this._curAnim.update();

        // Aktualizovat kolizni boxy
        let i = this._colBoxEntites.length;

        while (i-- != 0) {
            let ent = this._colBoxEntites[i];
            let node = this._colBoxNodes[i];

            ent.setPosition(node.getPosition());
            ent.setRotation(node.getRotation());
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleColBoxTriggerEnter(other: pc.Entity): void {
        if (this._entity.enabled && other.name == ePhysicsObjects.ball && this._curAnimType == 0) {
            // Odrazit mic od brankare
            Ball.instance.bounceOffGoalie();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleKickOff(resEstimate: KickOffResEstimate): void {
        if (!this._entity.enabled)
            return;

        this._targetBallPos.set(resEstimate.goaliePos.x, resEstimate.goaliePos.y);

        this._validAnims.clear(false);
        this._validAnimsMask = 0;
        this._validAnimsPriority = Number.MIN_SAFE_INTEGER;

        if (resEstimate.result != eKickOffResult.target) {
            this.chooseCatchAnim(resEstimate);
            this._curAnimType = 0;
        }
        else {
            this.chooseMissAnim(resEstimate);
            this._curAnimType = 1;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vyber animace pro minuti mice
     * @param resEstimate 
     */
    //-------------------------------------------------------------------------
    private chooseMissAnim(resEstimate: KickOffResEstimate): void {
        let ballPos = this._targetBallPos;
        let ballTime = resEstimate.goalieTime;
        let goaliePos = this._entity.getPosition();

        // Vybrat animaci        
        let rc = new Rectangle();

        Goalie.MISS_ANIMS.forEach((animData: GoalieMissAnim): void => {
            if ((this._validAnimsMask & (1 << animData.animId)) == 0) {
                let i = animData.priority;

                if (i == undefined)
                    i = 0;

                if (this._validAnimsPriority <= i) {
                    i = animData.areas.length;

                    while (i-- != 0) {
                        let area = animData.areas[i];

                        rc.setTo(goaliePos.x + area.x - area.z / 2, area.y - area.w / 2, area.z, area.w);

                        if (rc.contains(ballPos.x, ballPos.y)) {
                            this.addValidAnim(animData);
                            break;
                        }

                        // Presunout testovanou oblast na druhou stranu od brankare
                        if (area.x != 0) {
                            rc.x = goaliePos.x - area.x - area.z / 2;

                            if (rc.contains(ballPos.x, ballPos.y)) {
                                this.addValidAnim(animData);
                                break;
                            }
                        }
                    }
                }
            }
        });

        if (this._validAnims.empty)
            return;

        // Inicializovat animaci
        let animData = this._validAnims.getItemAtIndex(rnd2.randomIntIn(0, this._validAnims.size - 1)) as GoalieMissAnim;

        this._curAnim = this._missAnims[animData.animProcessor];
        this._curAnim.init(ballPos, ballTime, animData);
    }

    //-------------------------------------------------------------------------
    /**
     * Vyber animace pro chytnuti mice
     * @param resEstimate 
     */
    //-------------------------------------------------------------------------
    private chooseCatchAnim(resEstimate: KickOffResEstimate): void {
        let ballPos = this._targetBallPos;
        let ballTime = resEstimate.goalieTime;
        let goaliePos = this._entity.getPosition();

        // Je mic vlevo nebo vpravo od brany?
        let ballIsOutside = false;
        let ballHCenterOffset = Math.abs(ballPos.x);

        if (ballHCenterOffset > Stadium.GOAL_HALF_WIDTH) {
            if (ballHCenterOffset - Stadium.GOAL_HALF_WIDTH > 1.5)
                // Mic leti hodne mimo branu -> brankar nic neudela
                return;

            ballPos.x = (ballPos.x > 0 ? Stadium.GOAL_HALF_WIDTH : -Stadium.GOAL_HALF_WIDTH);
            ballIsOutside = true;
        }

        if (ballPos.y > Stadium.GOAL_HEIGHT) {
            ballPos.y = Stadium.GOAL_HEIGHT;
            ballIsOutside = true;
        }

        // Je mic nad branou?
        if (ballPos.y > Stadium.GOAL_HEIGHT) {
            ballPos.y = Stadium.GOAL_HEIGHT;
            ballIsOutside = true;
        }

        // Overit max vzdalenost mice od brankare na ose X
        if (Math.abs(ballPos.x - goaliePos.x) > Stadium.GOAL_HALF_WIDTH) {
            ballPos.x = goaliePos.x + (Stadium.GOAL_HALF_WIDTH * 0.9) * (ballPos.x > goaliePos.x ? 1 : -1);
        }

        // Vybrat animaci        
        let rc = new Rectangle();

        Goalie.CATCH_ANIMS.forEach((animData: GoalieCatchAnim): void => {
            if ((this._validAnimsMask & (1 << animData.animId)) == 0) {
                let i = animData.priority;

                if (i == undefined)
                    i = 0;

                if (this._validAnimsPriority <= i) {
                    i = animData.catchAreas.length;

                    while (i-- != 0) {
                        let area = animData.catchAreas[i];

                        rc.setTo(goaliePos.x + area.x - area.z / 2, area.y - area.w / 2, area.z, area.w);

                        if (rc.contains(ballPos.x, ballPos.y)) {
                            this.addValidAnim(animData);
                            break;
                        }

                        // Presunout testovanou oblast na druhou stranu od brankare
                        if (area.x != 0) {
                            rc.x = goaliePos.x - area.x - area.z / 2;

                            if (rc.contains(ballPos.x, ballPos.y)) {
                                this.addValidAnim(animData);
                                break;
                            }
                        }
                    }
                }
            }
        });

        if (this._validAnims.empty)
            return;

        // Inicializovat animaci
        let animData = this._validAnims.getItemAtIndex(rnd2.randomIntIn(0, this._validAnims.size - 1)) as GoalieCatchAnim;

        this._curAnim = this._catchAnims[animData.animProcessor];
        this._curAnim.init(ballPos, ballTime, animData);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private addValidAnim(anim: GoalieCatchAnim | GoalieMissAnim): void {
        if (anim.priority != undefined && anim.priority > this._validAnimsPriority) {
            // Odstranit animace s nizsi prioritou
            this._validAnimsPriority = anim.priority;
            this._validAnims.clear(false);
        }

        this._validAnims.add(anim);
        this._validAnimsMask |= (1 << anim.animId);
    }
}